import { Bell, History, Play } from "lucide-react";
import { makeReminder, useToggleReminder } from "@/components/Reminders";
import { useStore } from "@/lib/store";
import { useXC, useNow } from "@/lib/hooks";
import { asList, b64, fmtTime, fmtDay } from "@/lib/xc";

export function NowNext({ channel }) {
  const { active, hasReminder } = useStore();
  const toggleReminder = useToggleReminder();
  const now = useNow();
  const { data, loading } = useXC(active, "get_short_epg", { stream_id: channel.id, limit: 4 });
  const list = asList(data?.epg_listings).filter((p) => Number(p.stop_timestamp) * 1000 > now);
  if (loading) return <div className="text-sm text-slate-500">Loading guide…</div>;
  if (!list.length) return <div className="text-sm text-slate-500" data-testid="epg-empty">No guide information for this channel.</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="now-next">
      {list.slice(0, 2).map((p, i) => {
        const st = Number(p.start_timestamp) * 1000;
        const en = Number(p.stop_timestamp) * 1000;
        const pct = i === 0 ? Math.min(100, Math.max(0, ((now - st) / (en - st)) * 100)) : 0;
        return (
          <div key={p.id + i} className="rounded-xl border border-[#212738] bg-[#11141F] p-4">
            <div className="flex items-center gap-2">
              <div className={`xc-label flex-1 ${i === 0 ? "text-[#E50914]" : ""}`}>{i === 0 ? "Now" : "Next"} · {fmtTime(p.start_timestamp)} – {fmtTime(p.stop_timestamp)}</div>
              {i === 1 && (
                <button
                  onClick={() => toggleReminder(makeReminder(channel, p))}
                  className="p-1 rounded-md hover:bg-white/5"
                  title="Remind me when this starts"
                  data-testid="epg-next-reminder-button"
                >
                  <Bell className={`w-4 h-4 ${hasReminder(`${channel.id}:${p.start_timestamp}`) ? "fill-[#FFB800] text-[#FFB800]" : "text-slate-500"}`} />
                </button>
              )}
            </div>
            <div className="font-medium mt-1.5 truncate" data-testid={i === 0 ? "epg-now-title" : "epg-next-title"}>{b64(p.title)}</div>
            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{b64(p.description)}</div>
            {i === 0 && (
              <div className="h-1 rounded-full bg-[#23283B] mt-3 overflow-hidden">
                <div className="h-full bg-[#E50914]" style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const archived = (p, ch, now) => {
  const stop = Number(p.stop_timestamp) * 1000;
  const start = Number(p.start_timestamp) * 1000;
  const limit = now - (ch.archiveDays || 1) * 86400000;
  return stop < now && start > limit && (p.has_archive === undefined || Number(p.has_archive) === 1);
};

export function CatchupList({ channel, current, onPick }) {
  const { active } = useStore();
  const now = useNow(60000);
  const { data, loading } = useXC(active, "get_simple_data_table", { stream_id: channel.id });
  const past = asList(data?.epg_listings)
    .filter((p) => archived(p, channel, now))
    .sort((a, b) => Number(b.start_timestamp) - Number(a.start_timestamp))
    .slice(0, 40);
  return (
    <div data-testid="catchup-list">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-[#00E5FF]" />
        <h3 className="font-heading text-lg font-semibold">Catch-up</h3>
        <span className="xc-label">last {channel.archiveDays || 1} days</span>
      </div>
      {loading && <div className="text-sm text-slate-500">Loading past shows…</div>}
      {!loading && !past.length && <div className="text-sm text-slate-500">No past shows available.</div>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {past.map((p) => {
          const on = current?.start_timestamp === p.start_timestamp;
          return (
            <button
              key={p.start_timestamp}
              onClick={() => onPick({ ...p, titleText: b64(p.title) })}
              className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${on ? "border-[#00E5FF]/60 bg-[#00E5FF]/5" : "border-[#212738] hover:border-[#38425C] hover:bg-[#11141F]"}`}
              data-testid={`catchup-item-${p.start_timestamp}`}
            >
              <span className="w-8 h-8 rounded-full bg-[#1A1E2E] grid place-items-center shrink-0">
                <Play className="w-3.5 h-3.5 fill-white text-white ml-0.5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{b64(p.title)}</span>
                <span className="block text-[11px] font-mono text-slate-500">{fmtDay(p.start_timestamp)} · {fmtTime(p.start_timestamp)} – {fmtTime(p.stop_timestamp)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
